import { Link } from '@/components/LocalizedLink';
import { ArrowRight, Calculator } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getToolById } from '@/data/tools';
import { getLocalizedTool, toolsPageCopy, type ToolsLang } from '@/data/toolsLocale';
import { getBlogConversionCtaCopy } from '@/lib/blogConversion';
import { portfolioProjects } from '@/lib/portfolioProjects';

import { buildContactUrlFromBlog } from '@/lib/leadAttribution';

type BlogConversionPanelProps = {
  blogSlug: string;
  toolIds: string[];
  projectSlugs: string[];
};

const BlogConversionPanel = ({ blogSlug, toolIds, projectSlugs }: BlogConversionPanelProps) => {
  const { lang, t } = useLanguage();
  const copy = getBlogConversionCtaCopy(lang);
  const toolsLang = lang as ToolsLang;
  const toolsCopy = toolsPageCopy[toolsLang];

  const tools = toolIds
    .map((id) => {
      const base = getToolById(id);
      return base ? getLocalizedTool(base, toolsLang) : null;
    })
    .filter((tool): tool is NonNullable<typeof tool> => Boolean(tool));

  const projects = portfolioProjects.filter((p) => projectSlugs.includes(p.slug)).slice(0, 3);

  return (
    <div className="mt-16 space-y-12 max-w-3xl">
      <section className="rounded-2xl border border-border bg-card/60 p-8 sm:p-10">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">{copy.title}</h2>
        <p className="font-body text-muted-foreground leading-relaxed mb-8 max-w-2xl">{copy.subtitle}</p>
        <Link
          to={buildContactUrlFromBlog(blogSlug)}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-8 py-3.5 font-body text-sm font-medium text-background transition hover:opacity-90"
        >
          {copy.primary}
          <ArrowRight size={16} />
        </Link>
      </section>

      {tools.length > 0 ? (
        <section className="rounded-2xl border border-dashed border-border/80 bg-secondary/30 p-8">
          <div className="flex items-start gap-3 mb-5">
            <Calculator className="mt-1 h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <h3 className="font-display text-xl font-semibold text-foreground">{copy.toolsTitle}</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">{copy.toolsSubtitle}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {tools.map((tool) => (
              <Link
                key={tool.id}
                to={`/tools/${tool.id}`}
                className="flex-1 rounded-xl border border-border/70 bg-background px-5 py-4 hover:border-foreground/20 transition-colors"
              >
                <p className="font-display text-base text-foreground mb-1">{tool.title}</p>
                <p className="font-body text-xs text-muted-foreground line-clamp-2">{tool.tagline}</p>
              </Link>
            ))}
            <Link
              to="/tools"
              className="inline-flex items-center justify-center rounded-xl border border-border px-5 py-4 font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {toolsCopy.title} →
            </Link>
          </div>
        </section>
      ) : null}

      {projects.length > 0 ? (
        <section>
          <h3 className="font-display text-xl font-semibold text-foreground mb-4">{copy.projectsTitle}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Link
                key={project.key}
                to={`/projects/${project.slug}`}
                className="group rounded-xl border border-border/70 overflow-hidden bg-card/40"
              >
                <img
                  src={project.image}
                  alt={t(`projects.${project.key}.title`)}
                  className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-300"
                  loading="lazy"
                />
                <div className="p-3">
                  <p className="font-display text-sm text-foreground mb-1 line-clamp-2">
                    {t(`projects.${project.key}.title`)}
                  </p>
                  <span className="font-body text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                    {lang === 'tr' ? 'Case study →' : 'View case →'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default BlogConversionPanel;
